import { Experiment, SettingsAttributes } from '@pulse/common';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { PulseSettings } from './settings.service';

interface SettingsWrapped {
    // Legacy API - for backward compatibility
    get<T = any>(
        tag: string,
        settingName: string,
        updateSettingChange?: boolean,
    ):
        | Promise<T>
        | {
              value: T;
              subscribe: (callback: (value: T) => void, settingName: string) => Subscription;
          };

    // Modern API - preferred for new code
    getValue<T = any>(tag: string, settingName: string, defaultValue?: T): Promise<T>;

    observe<T>(tag: string, settingName: string, defaultValue: T): Observable<T>;
    getSegmentsIds(): Promise<string[]>;
    getExperiments(): Promise<Experiment[]>;
}

export class PulseSettingsFactory {
    constructor(private settingsService: PulseSettings) {}
    createPulseSettings(
        defaults: Record<string, any> | null | undefined = {},
        remoteSettingEnabled = true,
        attributes: SettingsAttributes = {},
    ): SettingsWrapped {
        const settingsService = this.settingsService;

        return {
            // Legacy API - handles both old and new signatures
            async get<T = any>(
                tag: string,
                settingName: string,
                updateSettingChange?: boolean,
            ): Promise<
                | T
                | {
                      value: T;
                      subscribe: (
                          callback: (value: T) => void,
                          settingName: string,
                      ) => Subscription;
                  }
            > {
                if (typeof updateSettingChange === 'boolean') {
                    console.warn(
                        `[PULSE] You are using a deprecated API. Please update to the latest Pulse version and ` +
                            `use getValue(tag, name, defaultValue) instead of get(tag, name, updateSettingChange). ` +
                            `Check the documentation for migration guidelines.`,
                    );
                }

                const def = defaults?.[settingName];

                if (updateSettingChange === true) {
                    const observable = settingsService.observe(
                        tag,
                        settingName,
                        def,
                        remoteSettingEnabled,
                        attributes,
                    ) as Observable<T>;

                    const initialValue = await firstValueFrom(observable);

                    return {
                        value: initialValue,
                        subscribe: observable.subscribe.bind(observable),
                    };
                } else {
                    return settingsService.get(
                        tag,
                        settingName,
                        def,
                        remoteSettingEnabled,
                        attributes,
                    );
                }
            },

            // Modern API - preferred for new code
            getValue<T = any>(tag: string, settingName: string, defaultValue?: T): Promise<T> {
                const def = defaultValue ?? defaults?.[settingName];

                return settingsService.get(tag, settingName, def, remoteSettingEnabled, attributes);
            },
            observe<T>(tag: string, settingName: string, defaultValue: T): Observable<T> {
                return settingsService.observe(
                    tag,
                    settingName,
                    defaultValue,
                    remoteSettingEnabled,
                    attributes,
                );
            },
            getSegmentsIds(): Promise<string[]> {
                return settingsService.getSegments(attributes);
            },
            getExperiments(): Promise<Experiment[]> {
                return settingsService.getExperiments(attributes);
            },
        } as any;
    }
}
